'use client'

import { useEffect, useRef } from 'react'
import {useAuth} from '@fitro/customer-host/auth'
import {usePathname} from 'next/navigation'
import { useNotifications } from './NotificationsProvider'
import { usePartnerStaffAccess } from '../../hooks/usePartnerStaffAccess'
import { useTranslation } from '../../hooks/useTranslation'
import { canAccessAdminDashboard } from '../../lib/staff-access'
import {isPublicTenantEntryPath} from '../../lib/tenant-seo-policy'

const PROMPT_DURATION_MS = 8000
const PROMPT_SESSION_KEY_PREFIX = 'fitro:admin-dashboard-prompt'

export const AdminDashboardPrompt: React.FC = () => {
    const { userId } = useAuth()
    const pathname = usePathname()
    const { notifyInfo, dismissToast } = useNotifications()
    const isPublicTenantPage = Boolean(pathname && isPublicTenantEntryPath(pathname))
    const { data, slug } = usePartnerStaffAccess({enabled: !isPublicTenantPage})
    const { t } = useTranslation('client')
    const lastPromptRef = useRef<string | null>(null)
    const lastToastIdRef = useRef<string | null>(null)
    const canOpenAdminDashboard = canAccessAdminDashboard(data)

    useEffect(() => {
        const attemptKey = slug ? `${userId ?? 'anonymous'}:${slug}` : null
        if (isPublicTenantPage || !canOpenAdminDashboard || !attemptKey) {
            if (lastToastIdRef.current) {
                dismissToast(lastToastIdRef.current)
                lastToastIdRef.current = null
            }
            lastPromptRef.current = null
            return
        }

        if (lastPromptRef.current === attemptKey) return
        lastPromptRef.current = attemptKey

        const sessionKey = `${PROMPT_SESSION_KEY_PREFIX}:${attemptKey}`
        try {
            if (window.sessionStorage.getItem(sessionKey)) return
            window.sessionStorage.setItem(sessionKey, 'shown')
        } catch {
            // Storage may be unavailable in privacy-restricted browser contexts.
        }

        const toastId = notifyInfo(t('notifications.adminDashboardAvailable'), {
            dedupeKey: `admin-dashboard:${attemptKey}`,
            durationMs: PROMPT_DURATION_MS,
            actionLabel: t('actions.openAdminDashboard'),
            onAction: () => {
                window.location.assign(new URL('/admin', window.location.origin))
            },
        })
        lastToastIdRef.current = toastId
    }, [canOpenAdminDashboard, dismissToast, isPublicTenantPage, notifyInfo, slug, t, userId])

    return null
}
